import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/hooks/useTheme';

interface StepLegalProfileProps {
  data: Record<string, string>;
  onChange: (data: Record<string, string>) => void;
  errors: Record<string, string>;
}

export default function StepLegalProfile({ data, onChange, errors }: StepLegalProfileProps) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#8E8E93' : '#6B6B70';
  const inputBg = isDark ? '#1A1A1A' : '#fff';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const placeholderColor = isDark ? '#6B6B70' : '#9CA3AF';

  const handleChange = (key: string, value: string) => { onChange({ ...data, [key]: value }); };
  const handleBlur = (key: string) => { setTouched((prev) => ({ ...prev, [key]: true })); };

  const inputClass = "w-full px-3 py-2.5 rounded-md text-sm border transition-colors outline-none";
  const inputStyle = (key: string) => ({
    borderColor: touched[key] && errors[key] ? 'var(--accent)' : inputBorder,
    background: inputBg,
    color: textMain,
  });

  const renderInput = (key: string, labelKey: string, phKey: string, required: boolean, fullWidth = false) => (
    <div key={key} className={fullWidth ? 'md:col-span-2' : ''}>
      <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>
        {t(labelKey)}{required && <span style={{ color: 'var(--accent)' }}>*</span>}
      </label>
      <input
        type="text"
        value={data[key] ?? ''}
        onChange={(e) => handleChange(key, e.target.value)}
        onBlur={() => handleBlur(key)}
        placeholder={t(phKey)}
        className={inputClass}
        style={inputStyle(key)}
        onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
      />
      {touched[key] && errors[key] && <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors[key]}</p>}
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="mb-6">
        <h2 className="text-xl font-semibold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
          {t('ob_s2_title')}
        </h2>
        <p className="text-sm mt-1" style={{ color: textMuted }}>
          {t('ob_s2_subtitle')}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderInput('registrationCountry', 'ob_s2_registrationCountry', 'ob_s2_registrationCountry_ph', true)}
        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>
            {t('ob_s2_vatStatus')}<span style={{ color: 'var(--accent)' }}>*</span>
          </label>
          <select
            value={data.vatStatus ?? ''}
            onChange={(e) => handleChange('vatStatus', e.target.value)}
            onBlur={() => handleBlur('vatStatus')}
            className="w-full px-3 py-2.5 rounded-md text-sm border transition-colors outline-none cursor-pointer"
            style={{
              borderColor: touched.vatStatus && errors.vatStatus ? 'var(--accent)' : inputBorder,
              background: inputBg,
              color: data.vatStatus ? textMain : placeholderColor,
            }}
          >
            <option value="" disabled>{t('ob_s2_vatStatus_ph')}</option>
            <option value="true">{t('ob_s2_vat_registered')}</option>
            <option value="false">{t('ob_s2_vat_not_registered')}</option>
            <option value="exempt">{t('ob_s2_vat_exempt')}</option>
          </select>
          {touched.vatStatus && errors.vatStatus && (
            <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors.vatStatus}</p>
          )}
        </div>
        {renderInput('fullLegalName', 'ob_s2_fullLegalName', 'ob_s2_fullLegalName_ph', true, true)}
        {renderInput('legalAddress', 'ob_s2_legalAddress', 'ob_s2_legalAddress_ph', true, true)}
        {renderInput('actualAddress', 'ob_s2_actualAddress', 'ob_s2_actualAddress_ph', false, true)}
        {renderInput('vatNumber', 'ob_s2_vatNumber', 'ob_s2_vatNumber_ph', false)}
        {renderInput('directorName', 'ob_s2_directorName', 'ob_s2_directorName_ph', true)}
        {renderInput('authorizedPerson', 'ob_s2_authorizedPerson', 'ob_s2_authorizedPerson_ph', false)}
        {renderInput('basisOfAuthority', 'ob_s2_basisOfAuthority', 'ob_s2_basisOfAuthority_ph', false, true)}
      </div>
    </div>
  );
}